import * as React from "react";

import { startCase } from "lodash";

import { newSetKeyValueChange } from "../../../helpers/Doc/changes";

import GenModal from "../../Gen/Modal/component";
import GenToggleView from "../../Gen/ToggleView/component";
import PdfViewerIframe from "../BlockNodeHelpers/PdfViewerIframe";

import { MEDIA_PICKER } from "../../../helpers/Doc/menu";

interface EditBarMediaPickerProps {
  userContext: UserContext;
  editingContext: EditingContextInEditor;
  editingContextExtra: EditingContextExtra;
  mediaContext: MediaContext;
};

const VIDEO_ID_ERROR_ID = "media-picker-video-id-error";

const VIDEO_ID_MESSAGES: Record<string, string> = {
  youtube: "Paste in the 11-digit video ID. This is found immediately following 'v=' in the URL.",
  vimeo: "Paste in the 9- to 10-digit video ID. This is a string of numerals in the URL.",
};

// A YouTube ID is always 11 URL-safe characters and a Vimeo ID is always numeric (any length).
const VIDEO_ID_PATTERNS: Record<string, RegExp> = {
  youtube: /^[\w-]{11}$/,
  vimeo: /^\d+$/,
};

export function videoIdError(source: string, uri?: string) {
  const id = (uri || "").trim();
  const pattern = VIDEO_ID_PATTERNS[source];

  if (!id || !pattern || pattern.test(id)) { return null; }

  return VIDEO_ID_MESSAGES[source];
}

function VideoRemoteIdFields({
  source,
  uri,
  uid,
  placeholder,
  addChange,
  closeCurrentMenu,
}: {
  source: string;
  uri?: string;
  uid: string;
  placeholder: string;
  addChange: EditingContextInEditor["addChange"];
  closeCurrentMenu: () => void;
}) {
  const [draftUri, setDraftUri] = React.useState(uri || "");
  const uriError = videoIdError(source, draftUri);

  const accept = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (uriError) { return; }
    addChange(newSetKeyValueChange(uid, "uri", draftUri.trim()));
    closeCurrentMenu();
  };

  return (
    <div>
      <input
        type="text"
        value={draftUri}
        onChange={(e) => setDraftUri(e.target.value)}
        placeholder={placeholder}
        aria-invalid={!!uriError}
        aria-describedby={uriError ? VIDEO_ID_ERROR_ID : undefined}
      />
      {uriError && (
        <p id={VIDEO_ID_ERROR_ID} role="alert" style={{ color: "#b02a37", fontSize: 14, marginTop: 8 }}>{uriError}</p>
      )}
      <div className="gen-modal-choice-buttons" style={{ justifyContent: "flex-end", marginTop: 16 }}>
        <button type="button" onClick={accept}>Accept</button>
      </div>
    </div>
  );
}

export default function EditBarMediaPicker(props: EditBarMediaPickerProps) {
  const {
    userContext,
    editingContext,
    editingContextExtra,
    mediaContext,
  } = props;

  const currentMenu = editingContextExtra.currentMenu;

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        editingContext.closeCurrentMenu();
      }
    };

    if (currentMenu?.menu === MEDIA_PICKER) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentMenu, editingContext]);

  if (currentMenu?.menu !== MEDIA_PICKER) { return null; }

  let innerContent;
  switch (currentMenu.mediaType) {
    case "image":
      const images = mediaContext.images.map((imageAsset) => {
        if(!userContext.permissions.editMediaAssets) {
          return (
            <div key={imageAsset.external_identifier}>
              <img src={imageAsset.distribution_url} />
              <p>{imageAsset.title}</p>
            </div>
          );
        }

        const makeImage = (e: React.MouseEvent<HTMLImageElement | HTMLButtonElement>) => {
          e.stopPropagation();

          if (editingContextExtra.currentMenuNode.type === "section") {
            editingContextExtra.setVersionCoverImage(imageAsset.distribution_url);
            editingContext.closeCurrentMenu();
          } else {
            editingContext.addChange(newSetKeyValueChange(currentMenu.uid, "url", imageAsset.distribution_url));
            editingContext.closeCurrentMenu();
          }
        };

        return (
          <div key={imageAsset.external_identifier}>
            <img onClick={makeImage} src={imageAsset.distribution_url} />
            <p>{imageAsset.title} <button onClick={makeImage}>Select</button></p>
          </div>
        );
      });

      innerContent = images.length > 0 ? images : <div key="no-images"><h3>No Image Files</h3><h6>Upload files to Case</h6></div>;
      break;
    case "audio":
      const audios = mediaContext.audios.map((audioAsset) => {
        if(!userContext.permissions.editMediaAssets) {
          return (
            <div key={audioAsset.external_identifier}>
              <audio preload="none" controls>
                <source src={audioAsset.distribution_url} />
              </audio>
              <p>{audioAsset.title}</p>
            </div>
          );
        }

        const makeAudio = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          editingContext.addChange(newSetKeyValueChange(currentMenu.uid, "url", audioAsset.distribution_url));
          editingContext.closeCurrentMenu();
        };

        return (
          <div key={audioAsset.external_identifier}>
            <audio preload="none" controls>
              <source src={audioAsset.distribution_url} />
            </audio>
            <p>{audioAsset.title} <button onClick={makeAudio}>Select</button></p>
          </div>
        );
      });

      innerContent = audios.length > 0 ? audios : <div key="no-audios"><h3>No Audio Files</h3><h6>Upload files to Case</h6></div>;
      break;
    case "video":
      if (editingContextExtra.currentMenuNode.type !== "video") { return }

      const videoNode = editingContextExtra.currentMenuNode;

      const possibleVideos = mediaContext.videos.map((videoAsset) => {
        let captionsTrack = null;
        const captions = mediaContext.captions.filter((c)=>{return c.title === videoAsset.title;});
        if (captions.length) {
          let caption = null;
          if (captions.length > 1) {
            caption = captions.reduce((capn_1, capn) => (capn_1.external_identifier > capn.external_identifier) ? capn_1 : capn);
          } else {
            caption = captions[0];
          }
          captionsTrack = <track key="en" label="English" kind="subtitles" srcLang="en" src={caption.distribution_url} />;
        }

        if(!userContext.permissions.editMediaAssets) {
          return (
            <div key={videoAsset.external_identifier}>
              <video crossOrigin="anonymous" preload="none" controls>
                <source src={videoAsset.distribution_url} />
                {captionsTrack}
              </video>
              <p>{videoAsset.title}</p>
            </div>
          );
        }

        const makeVideo = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();

          editingContext.addChange(newSetKeyValueChange(currentMenu.uid, "url", videoAsset.distribution_url));
          editingContext.closeCurrentMenu();
        };

        return (
          <div key={videoAsset.external_identifier}>
            <video crossOrigin="anonymous" preload="none" controls>
              <source src={videoAsset.distribution_url} />
              {captionsTrack}
            </video>
            <p>{videoAsset.title} <button onClick={makeVideo}>Select</button></p>
          </div>
        );
      });

      innerContent = (
        <GenToggleView
          activeKey={editingContextExtra.currentMenuNode.source}
          views={[{
            key: "local",
            name: "Local",
            onClick: () => {editingContext.addChange(newSetKeyValueChange(editingContextExtra.currentMenuNode.uid, "source", "local"));},
            view: <div>
              {possibleVideos.length > 0 ? possibleVideos : <div key="no-videos"><h3>No Video Files</h3><h6>Upload files to Case</h6></div> }
            </div>,
          },{
            key: "youtube",
            name: "YouTube",
            onClick: () => {editingContext.addChange(newSetKeyValueChange(editingContextExtra.currentMenuNode.uid, "source", "youtube"));},
            view: <VideoRemoteIdFields
              source="youtube"
              uri={videoNode.uri}
              uid={currentMenu.uid}
              placeholder={"Insert the YouTube video ID. For example... dQw4w9WgXcQ"}
              addChange={editingContext.addChange}
              closeCurrentMenu={editingContext.closeCurrentMenu}
            />,
          },{
            key: "vimeo",
            name: "Vimeo",
            onClick: () => {editingContext.addChange(newSetKeyValueChange(editingContextExtra.currentMenuNode.uid, "source", "vimeo"));},
            view: <VideoRemoteIdFields
              source="vimeo"
              uri={videoNode.uri}
              uid={currentMenu.uid}
              placeholder={"Insert the Vimeo video ID. For example... 123456789"}
              addChange={editingContext.addChange}
              closeCurrentMenu={editingContext.closeCurrentMenu}
            />,
          }]}
        />
      );
      break;
    case "pdf":
      const pdfs = mediaContext.pdfs?.map((pdfAsset) => {
        if (!userContext.permissions.editMediaAssets) {
          return (
            <div key={pdfAsset.external_identifier}>
              <a href={pdfAsset.distribution_url} target="_blank" rel="noopener noreferrer">{pdfAsset.title}</a>
            </div>
          );
        }

        const makePdf = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
          e.stopPropagation();
          editingContext.addChange(newSetKeyValueChange(currentMenu.uid, "url", pdfAsset.distribution_url));
          editingContext.closeCurrentMenu();
        };

        return (
          <div key={pdfAsset.external_identifier} style={{ marginBottom: 24 }}>
            <div style={{ width: "100%", height: 400, border: "1px solid #ccc", marginBottom: 8 }}>
              <PdfViewerIframe
                src={pdfAsset.distribution_url}
                width="100%"
                height="100%"
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>{pdfAsset.title}</span>
              {userContext.permissions.editMediaAssets && (
                <button onClick={makePdf}>Select</button>
              )}
            </div>
          </div>
        );
      }) || [];
      innerContent = pdfs.length > 0 ? pdfs : <div key="no-pdfs"><h3>No PDF Files</h3><h6>Upload files to Case</h6></div>;
      break;
    default:
      // Todo Add Error
      return null;
  }

  return (
    <GenModal open>
      <div className="gen-modal-panel gen-modal-media-picker" aria-labelledby="media-picker-modal-title">
        <div className="gen-modal-header">
          <div className="gen-modal-header-left">
            <h2 id="media-picker-modal-title">Pick {startCase(currentMenu.mediaType)}</h2>
          </div>
          <div className="gen-modal-header-right">
            <button onClick={(e) => {e.stopPropagation(); editingContext.closeCurrentMenu();}}>Cancel</button>
          </div>
        </div>
        <hr />
        {innerContent}
      </div>
    </GenModal>
  );
}
